import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {
  StoredSearchEditDialogComponent,
  StoredSearchEditDialogData
} from '../search-focus-edit-dialog/stored-search-edit-dialog.component';
import {StoredSearchServiceImplementation} from '../../stored-search.service';
import {CommonsService} from '../../../core/commons.service';
import {MatDialog} from '@angular/material/dialog';
import {SearchHandlerService} from '../../search-handler.service';
import {SearchContainer} from '../../../core/definitions/search-container';
import {StoredSearch} from '../../../core/definitions/stored-search';

@Component({
    selector: 'app-stored-search-menu-panel',
    templateUrl: './stored-search-menu-panel.component.html',
    styleUrls: ['./stored-search-menu-panel.component.scss'],
    standalone: false
})
export class StoredSearchMenuPanelComponent implements OnInit {
  // Injections
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly modalService: MatDialog = inject(MatDialog);
  private readonly searchHandler: SearchHandlerService = inject(SearchHandlerService);

  @Input() searchContainer: SearchContainer;
  @Input() fsi: StoredSearchServiceImplementation;
  @Output() closeStoredSearchPanel = new EventEmitter<boolean>();

  dialogOpen = false;
  displayStoredSearches: Array<StoredSearch> = [];
  showSelectStoredSearchPanel = false;
  searchQuery: string;
  storeMessage: string;

  ngOnInit(): void {
    if (!this.fsi.getCurrentStoredSearch()) {
      this.setCurrentStoredSearch(this.fsi.createNewStoredSearch());
    }
    if (this.fsi.getStoredSearches()) {
      this.getSetCurrentStoredSearch();
    }
    this.updateDisplayStoredSearch(null);
    if (this.displayStoredSearches.length > 0) {
      this.showSelectStoredSearchPanel = true;
    }
    this.toggleSettingMenu();
  }

  getStoredSearches(): StoredSearch[] {
    return this.fsi.getStoredSearches();
  }

  hasChanges() {
    return this.fsi.storedSearchHasChanges(this.searchContainer.storedSearch.curStoredSearch);
  }

  private getSetCurrentStoredSearch() {
    this.setCurrentStoredSearch(this.fsi.getCurrentStoredSearch());
  }

  private setCurrentStoredSearch(curStoredSearch: StoredSearch) {
    this.fsi.setCurrentStoredSearch(curStoredSearch ? curStoredSearch.storedSearchId : null);
    if (curStoredSearch && this.searchQuery !== undefined) {
      this.searchQuery = curStoredSearch.stored_search.name;
    }
    this.searchContainer.storedSearch.curStoredSearch = this.commons.copy(curStoredSearch);
  }

  toggleSelectStoredSearchPanel() {
    this.showSelectStoredSearchPanel = !this.showSelectStoredSearchPanel;
    if (this.showSelectStoredSearchPanel) {
      if (!this.displayStoredSearches) {
        if (this.searchQuery === undefined) {
          this.clearSearchInput();
        }
        this.updateDisplayStoredSearch(this.searchQuery);
      }
    }
    this.toggleSettingMenu();
  }

  async setStoredSearchAsNewSearch() {
    this.dialogOpen = true;
    const dialog = this.modalService.open(StoredSearchEditDialogComponent, {
      panelClass: 'edit-dialog',
      data: {
        fsi: this.fsi,
        storedSearchInEdit: this.searchContainer.storedSearch.curStoredSearch
      } as StoredSearchEditDialogData
    });
    const storedSearchNameQuery = await dialog.afterClosed().toPromise();

    this.dialogOpen = false;
    if (storedSearchNameQuery) {
      if (this.searchContainer.storedSearch.curStoredSearch.stored_search.name === '' && storedSearchNameQuery) {
        this.searchContainer.storedSearch.curStoredSearch.stored_search.name = storedSearchNameQuery;
      }
      try {
        let savedStoredSearch: StoredSearch;
        if (this.searchContainer.storedSearch.curStoredSearch.stored_search.stored) {
          savedStoredSearch = await this.fsi.storeStoredSearchAsNewStoredSearch(this.searchContainer.storedSearch.curStoredSearch, storedSearchNameQuery);
        } else {
          savedStoredSearch = await this.fsi.createOrUpdateStoredSearch(this.searchContainer.storedSearch.curStoredSearch);
        }
        if (savedStoredSearch) {
          this.setCurrentStoredSearch(savedStoredSearch);
        }
        this.setStoreMessage('TRANS__FOCUS__STORE_MESSAGE__FOCUS_STORED');
        this.updateDisplayStoredSearch(null);
        await this.selectTheStoredSearch(this.fsi.getCurrentStoredSearch());
      } catch (e) {
        console.error(e);
        this.setStoreMessage('TRANS__FOCUS__STORE_MESSAGE__NAME_EXISTS');
      }
      dialog.close();
      this.closeStoredSearchPanel.emit();
    }
  }

  async editStoredSearch(storedSearch: StoredSearch) {
    this.dialogOpen = true;
    const dialog = this.modalService.open(StoredSearchEditDialogComponent, {
      panelClass: 'edit-dialog',
      data: {
        fsi: this.fsi,
        originalStoredSearch: storedSearch.stored_search.isDefault,
        storedSearchInEdit: storedSearch,
        storedSearchNameQuery: storedSearch.stored_search.name
      } as StoredSearchEditDialogData
    });
    const storedSearchNameQuery = await dialog.afterClosed().toPromise();

    this.dialogOpen = false;
    if (storedSearchNameQuery) {
      if (storedSearch.stored_search.stored) {
        storedSearch.stored_search.name = storedSearchNameQuery;
        try {
          await this.fsi.createOrUpdateStoredSearch(storedSearch);
          this.setStoreMessage('TRANS__FOCUS__STORE_MESSAGE__FOCUS_CHANGES_STORED');
          this.updateDisplayStoredSearch(null);
        } catch (e) {
          console.error(e);
          this.setStoreMessage('TRANS__FOCUS__STORE_MESSAGE__NAME_EXISTS');
        }
      }
      this.closeStoredSearchPanel.emit();
    }
  }

  getStoredSearchNameWeighedWordBreak(storedSearch: StoredSearch) {
    return this.commons.applyWordBreakOpportunity(storedSearch.stored_search.$$optionNameWeighed);
  }

  async storeStoredSearchChanges() {
    try {
      await this.fsi.createOrUpdateStoredSearch(this.searchContainer.storedSearch.curStoredSearch);
      this.setStoreMessage('TRANS__FOCUS__STORE_MESSAGE__FOCUS_CHANGES_STORED');
    } catch (e) {
      console.error(e);
      this.setStoreMessage('TRANS__FOCUS__STORE_MESSAGE__NAME_EXISTS');
    }
  }

  clearSearchInput() {
    this.searchQuery = '';
    this.updateDisplayStoredSearch(this.searchQuery);
  }

  async delete(storedSearch: StoredSearch) {
    const storedSearchId = storedSearch.storedSearchId;
    await this.fsi.deleteStoredSearch(storedSearchId);
    this.updateDisplayStoredSearch(null);
    this.closeStoredSearchPanel.emit();
    if (storedSearchId === this.searchContainer.storedSearch.curStoredSearchId) {
      this.searchContainer.storedSearch.curStoredSearchId = null;
    }
    if (this.searchContainer.storedSearch.curStoredSearchId === null) {
      this.searchContainer.storedSearch.curStoredSearch = null;
      this.searchContainer.storedSearch.curStoredSearchName = null;
      await this.searchHandler.goPathView(this.searchContainer.path, this.searchContainer);
    }
  }

  async deselectStoredSearch() {
    if (this.searchContainer.storedSearch.curStoredSearchId) {
      this.searchContainer.storedSearch.curStoredSearch = null;
      this.searchContainer.storedSearch.curStoredSearchId = null;
      this.searchContainer.storedSearch.curStoredSearchName = null;
      await this.searchHandler.goPathView(this.searchContainer.path, this.searchContainer);
    }
  }

  async selectStoredSearch(storedSearch: StoredSearch) {
    if (storedSearch) {
      this.setStoreMessage(null);
      await this.fsi.loadStoredSearches();
      await this.selectTheStoredSearch(storedSearch);
      if (!this.searchQuery) {
        this.searchQuery = storedSearch.stored_search.name;
      }
      this.inputChange();
      this.closeStoredSearchPanel.emit();
    }
  }

  private setSelectedStoredSearch() {
    for (let displayStoredSearchIdx = 0 ; displayStoredSearchIdx < this.displayStoredSearches.length ; displayStoredSearchIdx++) {
      const displayStoredSearch = this.displayStoredSearches[displayStoredSearchIdx];
      displayStoredSearch.stored_search.marked = false;
      if (this.searchContainer.storedSearch.curStoredSearch && this.searchContainer.storedSearch.curStoredSearch.storedSearchId === displayStoredSearch.storedSearchId) {
        this.searchContainer.storedSearch.curStoredSearch.stored_search.marked = true;
        displayStoredSearch.stored_search.marked = true;
        const storedSearches = this.getStoredSearches();
        for (let storedSearchIdx = 0 ; storedSearchIdx < storedSearches.length ; storedSearchIdx++) {
          const storedSearch = storedSearches[storedSearchIdx];
          if (storedSearchIdx === displayStoredSearchIdx) {
            storedSearch.stored_search.marked = true;
          }
        }
      }
    }
  }

  private async selectTheStoredSearch(storedSearch: StoredSearch) {
    this.setCurrentStoredSearch(storedSearch);
    console.log(storedSearch.stored_search.homePath);
    await this.searchHandler.goPathView(storedSearch.stored_search.homePath, this.searchContainer, true);
  }

  inputChange() {
    this.updateDisplayStoredSearch(this.searchQuery);
  }

  private updateDisplayStoredSearch(query) {
    // Make copy of copies due to weight method setting properties on storedSearches that causes storedSearches to be set as changed
    const storedSearchesCopy: Array<StoredSearch> = this.commons.copy(this.getStoredSearches());
    this.displayStoredSearches = this.getWeightedAndOrderedOptions(storedSearchesCopy, query);
    this.setSelectedStoredSearch();
  }

  private getWeightedAndOrderedOptions(options: Array<StoredSearch>, query) {
    let res;
    const wOps = this.getWeightedOptions(options, query);
    res = query ? this.commons.sortArray(wOps, '$$weight') : wOps;
    return res;
  }

  private getWeightedOptions(options: Array<StoredSearch>, query) {
    const res = [];
    options.forEach((option) => {
      const oWeight = this.weightOption(option, query);
      option.stored_search.$$optionNameWeighed = oWeight.name;
      option.stored_search.$$weight = oWeight.weight;
      if (!query || oWeight.weight < 9) {
        res.push(option);
      }
    });
    return res;
  }

  private weightOption(option: StoredSearch, query) {
    let optionName, start, middle, end, endPos, qPos,
      startPos = 0, weight = 9, find;
    optionName = option.stored_search.name;
    if (query) {
      find = query.toLocaleUpperCase();
      do {
        qPos = optionName.toLocaleUpperCase().indexOf(find, startPos);
        if (qPos !== -1) {
          weight -= qPos === 0 ? 2 : 1;
          endPos = qPos + find.length;
          start = optionName.substring(0, qPos);
          middle = optionName.substring(qPos, endPos);
          end = optionName.substring(endPos);
          optionName = start + '<strong>' + middle +
            '</strong>';
          startPos = optionName.length;
          optionName += end;
        }
      } while (qPos !== -1);
    }
    return {
      name: optionName,
      weight: weight
    };
  }

  private setStoreMessage(message) {
    this.storeMessage = message;
    setTimeout(() => {
      this.setStoreMessage(null);
    }, 5000);
  }

  toggleSettingMenu(storedSearch?: StoredSearch) {
    this.displayStoredSearches.forEach(fo => {
      if (storedSearch) {
        if (fo.storedSearchId === storedSearch.storedSearchId) {
          fo.stored_search.settingMenu = !fo.stored_search.settingMenu;
        } else {
          fo.stored_search.settingMenu = false;
        }
      } else {
        fo.stored_search.settingMenu = false;
      }
      if (fo.storedSearchId === this.searchContainer.storedSearch.curStoredSearchId) {
        this.searchContainer.storedSearch.curStoredSearch.stored_search.settingMenu = fo.stored_search.settingMenu;
      }
    });
  }
  closeMenu() {
    this.toggleSettingMenu();
    this.closeStoredSearchPanel.emit();
  }
}
